import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Phone, Loader2, Flame, CalendarClock, PauseCircle, Snowflake } from 'lucide-react'
import { apiGet, postJson } from '../../../lib/api.js'
import { apiErrorText } from '../../../lib/apiError.js'
import { fmtDate, fmtTime } from '../../../lib/datetime.js'
import { usePersistedState } from '../../../lib/persist.js'
import { useMe } from '../../../lib/me.js'
import { useXanoRealtime } from '../../../lib/realtime.js'
import { lockState, LOCK_META } from '../../../lib/recallLock.js'
import { relabelBranch } from '../../../lib/asset.js'
import { HQ_BRANCH_ID, FALLBACK_BRANCH_ID } from '../../../lib/branch.js'
import {
  PRESETS, safePreset, presetById, fmtDay, VIEWS, viewById, STAGES, HOLD_TYPE_LABEL,
  COMBINED, EXTRACTION_LISTS, LIST_TYPE, safeCallList,
  parkReasonLabel, daysSince, cadenceHint, bkkTodayISO,
} from '../../../lib/implant.js'
import { quickCodesFor, RECALL_PREFIX, codedNote, splitNote, OUTCOME_BY_CODE } from '../../../data/implantOutcomes.js'
import { Badge, DataTable, PatientName, SubTabs, Segmented, FilterCard, Field, Select, BranchSelect } from '../../../kit'
import { OutcomePill } from '../../../components/recall/RecallNoteText.jsx'
import {
  REVIEW_STATUSES, reviewMeta, reviewStatusCode, REVIEWED_OPTS, reviewedParams,
  OUTCOME_BY_ID, STEP_LABEL, RESULT_LABEL,
} from '../../../lib/implantReview.js'
import { Card, Notice, fmt, asList } from '../../statistic/statShared.jsx'
import RecallPopup from '../../../components/recall/RecallPopup.jsx'
import ImplantType from './ImplantType.jsx'
import PatientStatus from './PatientStatus.jsx'

/* ────────────────────────────────────────────────────────────────────────────
 * TABS รีวิว AND โทร — ถอนฟันแล้วยังไม่ทดแทน. One component, two jobs (`mode`, Ram
 * 2026-10-29: deciding who gets a call and making the call are different people at
 * different times, so they no longer share one table):
 *
 *   mode 'review'  แท็บรีวิว. รอรีวิว = the lists at ยังไม่รีวิว or รอตรวจซ้ำ (api 678
 *                  review_status 1 / 2 — step 1 and step 2 are different reviewers),
 *                  and set apart from them ไม่ต้องโทรตอนนี้ = the views H / P / R.
 *                  Opening a row NEVER claims the call lock (a reviewer reading a chart
 *                  must not block the caller): the drawer's lock bar is read-only and
 *                  its recall notes are read-only (history, no add box).
 *   mode 'call'    แท็บรอโทร. พร้อมโทร only (review_status 3 — passed both steps; api 678
 *                  also counts W, back-from-พักไว้ and brought-back patients as ready).
 *                  This is the call queue described below.
 * The list buttons count by review status (api 678 review_view_counts): awaiting
 * review in one tab, พร้อมโทร in the other, so neither counts the other's people.
 * `onCounts` hands both totals up for the page's tab badges.
 *
 * THE CALL QUEUE IS NOT A REPORT. The difference is the lock: opening a row
 * claims the SAME patient-level lock (`patient.lock` / `lock_by` /
 * `lock_timestamp`) that /follow-ups/recall uses, so a สปสช. recall caller and
 * the implant caller see each other automatically — across 1,702 + 1,330
 * overlapping names, with no new code. That is plan.md §6.4 rule 2, met for free.
 *
 * Source: GET /api/implant/queue → Xano /report/implant_queue (api 678, claude
 * group; 2026-10-29 — api 650 plus rounds, list W, the views H / P / R and stages,
 * implant/report.md §6h). Server-paged at 50. One row = ONE PATIENT.
 *
 * LISTS vs VIEWS (2026-10-29). PRESETS are call lists, W first — the warm leads,
 * marked Warm Case. VIEWS (รอวันนัด · พักไว้ · พักจนกว่าจะมีรอบใหม่) hold the people NOT to
 * call right now; they live in แท็บรีวิว only, and the choice is never persisted. The
 * old "ดูคนที่ไม่เหมาะ/ทำที่อื่นแล้ว" toggle became the R view.
 *
 * 🚨 NO CSV EXPORT, DELIBERATELY. plan.md §6.4 rule 1 exists to get the
 * 3,120-row PII file OFF the salesperson's laptop. Shipping an export button
 * would re-create the exact artefact this page replaces.
 *
 * วันที่อ้างอิง AND หมายเหตุ ARE SHOWN OPENLY. Until 2026-09-17 they sat behind a
 * per-row ยืนยันตัวตนแล้ว button; Ram removed it. It hid an extraction date and a
 * tooth count, while clicking the row — which is how a call is claimed — opens
 * RecallPopup on the patient's full treatment and appointment tables with no gate
 * at all. So it protected nothing, and it was only ever a screen cover: the
 * values were always in the response. The RULE still stands and it is spoken,
 * not clicked — confirm who answered before saying anything about treatment. It
 * belongs in Pan's call script (plan.md §7.1, §8 item 14); the line under the
 * table repeats it. Do not re-add the mask without also gating the popup.
 *
 * รีวิวก่อนโทร (2026-09-17, Ram's three steps). Step 1 คัดกรอง and step 2 ตรวจซ้ำ are
 * buttons in the popup (ImplantReviewSection); step 3 is the call, unchanged. Since
 * 2026-10-29 steps 1-2 are แท็บรีวิว and step 3 is แท็บรอโทร (see the top); what
 * happened after a call is the ผลการโทร tab's log (CallResults, api 680). The rule
 * behind all of it: src/lib/implantReview.js.
 * ──────────────────────────────────────────────────────────────────────────── */

const PER_PAGE = 50

/* ── the last answer for each view, in memory only ────────────────────────────
 * Going to ผลเคสปรึกษา unmounts this component; coming back — or back to a list
 * already looked at this session — used to start again from a spinner. The last
 * response for each exact view (list · branch · recall days · page · review)
 * is kept HERE, in module memory, and shown at once while the page re-reads.
 * Memory only, never localStorage/sessionStorage: it is names and phone numbers.
 * 🔑 Rows shown from it are NOT clickable until the fresh answer for that view
 * lands (`fresh` below), so a patient excluded, cancelled or claimed since can
 * never be opened off an old copy. */
const lastByView = new Map()
const MAX_VIEWS = 20
function remember(key, value) {
  lastByView.delete(key)
  lastByView.set(key, value)
  while (lastByView.size > MAX_VIEWS) lastByView.delete(lastByView.keys().next().value)
}
// The nightly snapshot runs at 07:05 Bangkok; older than this means a run failed.
const SNAPSHOT_STALE_MS = 26 * 60 * 60 * 1000

export default function GapQueue({ mode = 'call', onCounts }) {
  const reviewMode = mode === 'review'
  const me = useMe()
  const meId = me?.employee_id

  // Each tab remembers its own list (the page keys this component by mode). แท็บรอโทร
  // opens on every type at once (Q) — a new key, so an older build's saved list does not
  // override that default (Ram, 2026-10-29).
  const [presetRaw, setPreset] = usePersistedState(reviewMode ? 'imp:rvPreset' : 'imp:callList', reviewMode ? 'C' : 'Q')
  // A link from the ผลการโทร tab opens one list (?tab=call&list=W). It wins from the first
  // render (so no request goes out for the old list), is saved as the choice, and leaves
  // the address — a refresh then keeps whatever the caller picks next.
  const [params, setParams] = useSearchParams()
  const listParam = presetById(params.get('list')) ? params.get('list') : ''
  const preset = reviewMode ? safePreset(listParam || presetRaw) : safeCallList(listParam || presetRaw)
  useEffect(() => {
    if (!params.has('list')) return
    if (listParam) setPreset(listParam)
    setParams((p) => { p.delete('list'); return p }, { replace: true })
  }, [params, listParam, setPreset, setParams])
  // A view across the lists (H / P / R) — แท็บรีวิว only, and not persisted: the tab
  // opens on รอรีวิว.
  const [view, setView] = useState('')
  const isView = reviewMode && !!viewById(view)
  const list = isView ? view : preset
  const meta = isView ? viewById(view) : (COMBINED[preset] || presetById(preset))

  const [branchSaved, setBranchSel] = usePersistedState('imp:branch', '')
  // A browser that saved สนามเป้า before it left the list opens on อารีย์ instead.
  const branchSel = Number(branchSaved) === HQ_BRANCH_ID ? String(FALLBACK_BRANCH_ID) : branchSaved
  // "อายุ 55+" and "ซ่อนแถวที่คนอื่นถืออยู่" are gone from every tab (Ram, 2026-10-29).
  // Removed, not hidden, so a choice saved by an older build cannot keep filtering
  // unseen: min_age is always 0 and every row on the page is shown.
  // The review state each tab shows. แท็บรีวิว: ยังไม่รีวิว or รอตรวจซ้ำ, persisted (a
  // reviewer works one step over several sittings). แท็บรอโทร: พร้อมโทร, fixed.
  const [rvSaved, setRvStatus] = usePersistedState('imp:rvStatus', 'none')
  const rvStatus = rvSaved === 'wait' ? 'wait' : 'none'
  const reviewSel = reviewMode ? rvStatus : 'ready'
  // "รีวิวล่าสุด" (approved within N days …) — แท็บรอโทร only, named in the title when on.
  const [reviewedSaved, setReviewed] = usePersistedState('imp:reviewed', '')
  const reviewed = !reviewMode && REVIEWED_OPTS.some((o) => o.v === reviewedSaved) ? reviewedSaved : ''
  const reviewFiltered = !isView && !!reviewed
  // "ซ่อนคนที่เพิ่งโทรไป" is GONE (Ram, 2026-10-30: "this page should only have patients
  // still not recalled"). Every implant result already takes the patient off รอโทร by its
  // own rule (พักไว้ · รอรอบใหม่ · รอวันนัด · ยกเลิกเคส · the 3-day ไม่รับสาย wait), so all
  // it still did was hide people ANOTHER department had phoned — 55 never-called patients
  // on 2026-10-30, and anyone a reviewer left a plain note on. A colleague on the case
  // shows as กำลังติดตาม; earlier calls are เคยโทร N and the drawer's history. api 678
  // keeps the input; the CRM always sends 0.
  const recalledDays = 0

  const [page, setPage] = useState(1)
  // Every input that changes WHICH rows api 650 returns. A response is only ever
  // rendered under the key it was fetched for — the old rows of another view are
  // never left on screen, clickable, under this view's title while it loads.
  const viewKey = [mode, list, branchSel, recalledDays, page,
    isView ? '' : reviewSel, isView ? '' : reviewed].join('|')
  const [data, setData] = useState(() => {
    const kept = lastByView.get(viewKey)
    return kept ? { ...kept, _fresh: false } : null   // never clickable before a fresh answer
  })
  const [st, setSt] = useState({ loading: true, err: '' })
  const [tick, setTick] = useState(0)
  const current = data && data._key === viewKey ? data : null
  const fresh = !!current?._fresh
  // A filter change goes back to page 1 in the SAME render as the change, so it is
  // one request. (A separate effect used to fetch the old page first, then page 1.)
  const refilter = (setter) => (v) => { setter(v); setPage(1) }

  /* ── one population query at a time, per browser ──────────────────────────
   * Since 2026-09-17 api 650 reads a nightly snapshot and answers in ~0.1-0.8 s
   * (it was 2 s warm and 17-46 s cold — implant/report.md §6a, §6b). The rules
   * below still matter: a browser abort does NOT stop a query on Xano, so
   * overlapping requests still pile up there.
   *
   * 🔑 THE PAGE'S OWN CONCURRENCY WAS THE BIGGEST EFFECT, not the query.
   * Measured off api 650's request history, 2026-09-08:
   *     serial:            3.7 · 3.9 · 4.3 · 4.7 · 4.9 · 5.0 · 5.0 · 6.0 · 6.1 s
   *     5 overlapping:    36.2 · 36.2 · 54.0 · 56.4 · 76.3 s
   * A 6-20x multiplier, and this page used to generate exactly that shape:
   * working ONE patient fires three refetches (claim, note saved, close), a
   * colleague's lock event fans one out to every open page, and nothing here
   * cancelled or coalesced any of them.
   *
   * So: a filter/page change ABORTS the in-flight query (its answer is now the
   * wrong answer), while a refetch COALESCES (its answer is still the right
   * answer — it just needs to be re-read once when the current one lands).
   * Never the other way round. */
  const acRef = useRef(null)
  const inFlightRef = useRef(false)
  const pendingRef = useRef(false)
  const refetch = useCallback(() => {
    if (inFlightRef.current) { pendingRef.current = true; return }
    setTick((t) => t + 1)
  }, [])

  const [selected, setSelected] = useState(null)
  // Rows noted THIS SESSION. Belt and braces: whether Xano's POST /recall bumps
  // patient.last_recall / recall_count is unverified — functions/api/recall/[id].js
  // documents that DELETE does not decrement them, which implies but does not
  // prove that ADD maintains them. If it does not, this Set is what makes the
  // queue advance instead of handing back the same name.
  const [worked, setWorked] = useState(() => new Set())

  const heldRef = useRef(null)

  useEffect(() => {
    let alive = true
    const key = viewKey
    // Any request that starts now answers every refetch that was waiting.
    pendingRef.current = false
    // Same view (a refetch) → keep what is on screen. Another view → its last answer
    // from this session, not clickable until fresh, or the spinner.
    setData((d) => {
      if (d && d._key === key) return d
      const kept = lastByView.get(key)
      return kept ? { ...kept, _fresh: false } : null
    })
    // Supersede the previous query. api.js has always taken a `signal` for
    // exactly this ("every keystroke stacks another multi-second query on Xano
    // and the newest one gets SLOWER, not faster") — this page just never
    // passed one. Aborting also hands the 6-slot read gate its slot back.
    acRef.current?.abort()
    const ac = new AbortController()
    acRef.current = ac
    inFlightRef.current = true
    setSt({ loading: true, err: '' })
    apiGet('/api/implant/queue', {
      list,
      branch_id: branchSel || 0,
      min_age: 0,
      recalled_days: recalledDays,
      page,
      per_page: PER_PAGE,
      review_status: isView ? 0 : reviewStatusCode(reviewSel),
      ...(isView ? { reviewed_within: 0, not_reviewed_within: 0 } : reviewedParams(reviewed)),
    }, { signal: ac.signal })
      .then((r) => {
        if (!alive) return
        const v = { ...r, _key: key, _fresh: true, _at: Date.now() }
        remember(key, v)
        setData(v)
        setSt({ loading: false, err: '' })
      })
      // An abort is this component superseding itself, not a failure — showing
      // "โหลดรายชื่อไม่สำเร็จ" for it would make every filter change look broken.
      .catch((e) => {
        if (e?.name === 'AbortError' || !alive) return
        setData(null); setSt({ loading: false, err: apiErrorText(e, 'โหลดรายชื่อไม่สำเร็จ') })
      })
      .finally(() => {
        if (acRef.current !== ac) return   // a newer query owns the state now
        inFlightRef.current = false
        if (pendingRef.current) { pendingRef.current = false; setTick((t) => t + 1) }
      })
    // Abort on unmount too, not just on supersede: navigating away from the page
    // otherwise leaves a 4-6 s query running and holding one of the six read slots.
    return () => { alive = false; ac.abort() }
    // viewKey is derived from exactly the inputs below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, branchSel, recalledDays, page, tick, isView, reviewSel, reviewed])

  /* ── the lock, copied from Recall.jsx so both pages behave identically ──── */
  // The patient this caller is trying to hold RIGHT NOW. heldRef only changes once a
  // claim has landed, so a popup closed (double-click on the backdrop, Esc) while
  // the claim is still in flight used to leave that patient locked with nothing
  // open — and there is no expiry. Every claim re-checks this after it lands.
  const wantRef = useRef(null)
  const lockCall = async (patient_id, request_type) => {
    // best-effort. ⚠️ NOT healed by an expiry — there isn't one (see recallLock.js);
    // a lock that fails to release stays lit until someone takes it over (รับต่อ).
    try { await postJson('/api/recall/lock', { patient_id, request_type }) } catch { /* takeover is the remedy */ }
  }
  // A claim only counts if the caller still wants that patient when it lands;
  // otherwise it is let go at once, AFTER landing, so the release cannot overtake it.
  // Returns whether the lock is held. heldRef is never overwritten by a claim the
  // caller has already abandoned (a slow claim for P landing after a claim for Q).
  const claim = async (p) => {
    await lockCall(p, 'claim')
    if (String(wantRef.current) !== String(p)) {
      await lockCall(p, 'release')
      refetch()   // the row may have been painted "held by me" meanwhile
      return false
    }
    heldRef.current = p
    return true
  }
  const release = async (p) => {
    if (p == null) return
    await lockCall(p, 'release')
    // A release that lands after the caller re-opened the same patient must not
    // forget the newer claim.
    if (String(heldRef.current) === String(p) && String(wantRef.current) !== String(p)) heldRef.current = null
  }
  const openRow = async (r) => {
    const p = r?.patient_id
    if (p == null) return
    // แท็บรีวิว (its lists and its views): open the record — to review it, or to bring
    // a parked patient back — but never claim the call lock: nobody is calling them.
    if (reviewMode) { setSelected(r); return }
    setSelected(r)
    wantRef.current = p
    if (heldRef.current != null && String(heldRef.current) !== String(p)) await release(heldRef.current)
    // Closed, or moved on, while the previous lock was being released → no claim.
    if (String(wantRef.current) !== String(p)) return
    if (await claim(p)) refetch()
  }
  const closePopup = async () => {
    const p = selected?.patient_id ?? null
    setSelected(null)
    wantRef.current = null
    // Release only what this caller holds. In review view nothing was claimed, and
    // release does not check who holds the lock — it would free a colleague's.
    if (String(heldRef.current) === String(p)) await release(p)
    refetch()
  }
  const popupLockAction = async (request_type) => {
    const p = selected?.patient_id ?? null
    if (p == null) return
    if (request_type === 'claim') {
      // Same rule as openRow: one patient held at a time, or the previous one is orphaned.
      wantRef.current = p
      if (heldRef.current != null && String(heldRef.current) !== String(p)) await release(heldRef.current)
      if (String(wantRef.current) !== String(p)) return
      if (!(await claim(p))) return
    } else {
      wantRef.current = null
      await release(p)
    }
    refetch()
  }
  // release on unmount — best-effort, and nothing backstops it: there is no
  // expiry window. A missed release leaves the row lit until someone takes over.
  useEffect(() => () => {
    wantRef.current = null   // a claim landing after navigating away releases itself
    if (heldRef.current != null) lockCall(heldRef.current, 'release')
  }, [])

  // A note was saved → grey the row now, and let the next fetch drop it.
  const onNoteSaved = () => {
    const p = selected?.patient_id
    if (p != null) setWorked((s) => new Set(s).add(String(p)))
    refetch()
  }

  // Someone else claimed/released a patient, or a patient's STATUS changed
  // anywhere in the clinic (the `patient_update - patient` trigger, 2026-09-09)
  // → refetch, debounced. Skip our own echoes; our own actions already refetch.
  // Status events carry no employee_id (table 19 has no modified_by) so they are
  // never skipped, and they arrive as `payload.data` rather than `payload`.
  // 🔑 Only events about a patient ON THIS SCREEN (or in the open popup) refetch.
  // The recall desk alone sent 753 lock events and 348 status events on
  // 2026-09-16; every one of them used to re-read this page for every open copy.
  // A patient off screen changes nothing here — a status change that adds someone
  // to list B shows on the next load. An event with no patient_id still refetches.
  const liveTimer = useRef(null)
  const onScreenRef = useRef(new Set())
  useEffect(() => {
    const ids = new Set(asList(current?.rows).map((r) => String(r.patient_id)))
    if (selected?.patient_id != null) ids.add(String(selected.patient_id))
    onScreenRef.current = ids
  }, [current, selected])
  useXanoRealtime('recall_update', (msg) => {
    const p = msg?.payload?.data ?? msg?.payload
    const emp = p && typeof p === 'object' ? p.employee_id : null
    if (emp != null && meId != null && String(emp) === String(meId)) return
    const pid = p && typeof p === 'object' ? p.patient_id : null
    // Filter only against a fresh, settled screen. While a read is in flight (or
    // after an error) the screen does not show what the next answer will, so an
    // event then falls through to refetch(), which just marks one re-read pending.
    if (pid != null && fresh && !inFlightRef.current && !onScreenRef.current.has(String(pid))) return
    clearTimeout(liveTimer.current)
    liveTimer.current = setTimeout(refetch, 400)
  })
  useEffect(() => () => clearTimeout(liveTimer.current), [])

  const rows = useMemo(() => asList(current?.rows), [current])

  const total = Number(current?.total_rows) || 0
  // Who api 650 hid from this list, and why. Both are hard rules; say so.
  const excludedHidden = Number(current?.excluded_hidden) || 0   // เคสไม่เหมาะ / ไปทำที่อื่นแล้ว
  const cancelledHidden = Number(current?.cancelled_hidden) || 0 // status ยกเลิกเคส
  // Rows on THIS page that the live re-check dropped: an ortho/implant bill or ortho
  // appointment since the snapshot was built. They leave the list at the next build.
  const recheckHidden = Number(current?.recheck_hidden) || 0
  const dataAsOf = Number(current?.data_as_of) || 0
  // Stale = the SERVER handed back an old build: judged at the moment of the fetch,
  // not at render, so a page left open overnight does not cry wolf. The build is
  // overdue if the snapshot's UTC date is behind the fetch's UTC date 25 minutes
  // after build time — or, whatever the date, it was older than 26 h when fetched.
  const fetchedAt = new Date(Number(current?._at) || Date.now())
  const snapshotStale = dataAsOf > 0 && (
    fetchedAt.getTime() - dataAsOf > SNAPSHOT_STALE_MS ||
    (String(current?.snapshot_date || '') < fetchedAt.toISOString().slice(0, 10) &&
      fetchedAt.getUTCHours() * 60 + fetchedAt.getUTCMinutes() >= 30))
  // A page left open across the morning build re-reads when the caller comes back
  // to it, instead of showing yesterday's population without a warning.
  useEffect(() => {
    const onBack = () => {
      if (document.visibilityState !== 'visible' || !current?._at) return
      if (new Date(current._at).toISOString().slice(0, 10) < new Date().toISOString().slice(0, 10)) refetch()
    }
    document.addEventListener('visibilitychange', onBack)
    window.addEventListener('focus', onBack)
    return () => { document.removeEventListener('visibilitychange', onBack); window.removeEventListener('focus', onBack) }
  }, [current, refetch])
  const pageTotal = Math.max(1, Math.ceil(total / PER_PAGE))
  // The list shrank under this page (a note moved someone out, the live re-check
  // dropped rows): go back to the last real page instead of sitting on an empty one.
  // Only from a fresh answer, so a remembered total can never trigger it.
  useEffect(() => { if (fresh && page > pageTotal) setPage(pageTotal) }, [fresh, page, pageTotal])

  /* The list's columns. 12 became 5 (Ram, 2026-10-29: "the main table is too long … strict
   * to design kit"): each small fact folds under the one it belongs to, nothing is dropped.
   *   คนไข้     name (nickname) · Warm Case, then HN · age · branch — kit PatientName, the
   *             name and HN open the patient in a new tab (decision #4; the old link
   *             column is gone), the row opens the drawer
   *   สถานะ     patient status, the review under it
   *   เบอร์โทร  the number, then who holds the call and how many times they were called
   *   ref date  the date, then the round's detail, the note and the teeth
   *   นัดถัดไป
   * Fixed widths (Ram, 2026-10-30: "the spacing looks odd"): in the auto layout one long
   * note or name stretched its column until the table ran past the card and clipped
   * นัดถัดไป. Now every column but คนไข้ has its width and what is longer truncates/wraps.
   * A remembered copy may show someone cancelled or excluded since: no number and no
   * patient link until the fresh answer. */
  const callColumns = [
    {
      key: 'patient', label: 'คนไข้',
      render: (r) => (
        <PatientName patient={{ id: fresh ? r.patient_id : 0, name: r.name, hn: r.hn }} avatar
          nickname={r.nickname}
          badge={reviewMode && r.list === 'W' ? <Badge tone="amber">Warm Case</Badge> : null}
          meta={[r.age != null ? `${r.age} ปี` : null, relabelBranch(r.branch)].filter(Boolean).join(' · ')} />
      ),
    },
    // แท็บรอโทร: what each row is, now that every type shares one list (ImplantType, the
    // same cell as ผลการโทร's log).
    ...(reviewMode ? [] : [{ key: 'type', label: 'ประเภท', width: '132px', render: (r) => <ImplantType list={r.list} /> }]),
    {
      key: 'status', label: 'สถานะ', width: '160px',
      render: (r) => (
        <span className="flex flex-col items-start gap-1">
          <PatientStatus status={r.patient_status} />
          {/* แท็บรอโทร only, and without its พร้อมโทร or ยังไม่โทร pill — everyone in that
              tab is both; what is left is a call that did happen. In แท็บรีวิว the step
              picker already says ยังไม่รีวิว / รอตรวจซ้ำ, and a view says why someone is
              parked in the date column. */}
          {!reviewMode && <ReviewCell r={r} hideStatus />}
        </span>
      ),
    },
    {
      key: 'phone', label: 'เบอร์โทร', width: '128px',
      render: (r) => {
        // The lock shows only when someone holds the patient — "ว่าง" on every row
        // was noise (Ram, 2026-10-29).
        const ls = lockState(r, meId).status
        const lm = LOCK_META[ls]
        return (
          <>
            <span className="block whitespace-nowrap tabular-nums">
              {!fresh ? <span className="text-slate-300">•••</span> : (r.phone || <span className="text-slate-300">—</span>)}
            </span>
            <span className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[11.5px] text-slate-400">
              {ls !== 'free' && (
                <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-md border px-1.5 py-px text-[10.5px] ${lm.cls}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${lm.dot}`} />{lm.label}
                </span>
              )}
              <span className="whitespace-nowrap tabular-nums">เคยโทร {r.recall_count || 0}</span>
            </span>
          </>
        )
      },
    },
    {
      key: 'ref', label: meta?.refDateLabel || 'วันที่อ้างอิง', width: '290px',
      render: (r) => (
        <>
          <span className="block whitespace-nowrap tabular-nums">
            {fmtDay(r.ref_date)}
            {/* every type in one list: say what the date is */}
            {preset === 'Q' && LIST_TYPE[r.list] && <span className="ml-1.5 text-[11.5px] text-slate-400">{LIST_TYPE[r.list].ref}</span>}
          </span>
          <span className="block text-[12px] text-slate-500">
            <RowDetail r={r} today={today} />
            {/* R view: NO / OC reads as the chip it came from (api 678 excluded_code) */}
            {r.excluded_code
              ? <><OutcomePill note={splitNote(codedNote(r.list || preset, r.excluded_code))} />{r.note && <span className="ml-1.5">{r.note}</span>}</>
              : r.note}
            {/* api 650 (2026-10-29): the teeth written on the latest extraction visit;
                ซี่ 8 = all of them wisdom teeth — the reviewer's quick drop. */}
            {r.teeth && <span className="ml-1.5 text-[11px] text-slate-400 tabular-nums">#{r.teeth.split(' ').join(' #')}</span>}
            {r.only8 && <span className="ml-1.5 whitespace-nowrap rounded px-1 py-px text-[10px] ring-1 bg-amber-50 text-amber-700 ring-amber-200">ซี่ 8</span>}
          </span>
        </>
      ),
    },
    {
      key: 'next', label: 'นัดถัดไป', width: '100px',
      render: (r) => <span className="whitespace-nowrap tabular-nums text-slate-500">{r.next_appt ? fmtDay(r.next_appt) : <span className="text-slate-300">—</span>}</span>,
    },
  ]
  // The chips code the note with the ROW's list ([IMP|W|…] vs [IMP|C|…]) — in the combined
  // list the tab's own choice (Q / T) is not a list, and W calls are counted apart.
  const chipList = selected?.list || preset
  const chips = useMemo(() => quickCodesFor(chipList), [chipList])
  // api 678 view_counts (per view) and review_view_counts (per list × review state,
  // "C:ready"; "*:…" = every list) for the buttons. Kept from the last fresh answer so
  // the buttons do not blank while another list loads.
  const [counts, setCounts] = useState({})
  const [rvCounts, setRvCounts] = useState(null)
  useEffect(() => {
    if (!fresh) return
    const parse = (v) => {
      if (typeof v === 'string') { try { v = JSON.parse(v) } catch { return null } }
      return v && typeof v === 'object' ? v : null
    }
    const o = parse(current?.view_counts)
    if (o) setCounts(o)
    const r = parse(current?.review_view_counts)
    if (r) {
      setRvCounts(r)
      const n = (k) => Number(r[k]) || 0
      onCounts?.({ review: n('*:none') + n('*:wait'), call: n('*:ready') })
    }
  }, [current, fresh, onCounts])
  // A list counts the people THIS tab works: awaiting review (ยังไม่รีวิว + รอตรวจซ้ำ)
  // here, พร้อมโทร there. Never the list's whole count — that would count the other
  // tab's people. A view counts itself.
  const RV_KEYS = reviewMode ? ['none', 'wait'] : ['ready']
  const countOf = (id) => {
    if (viewById(id)) return counts[id] == null ? null : Number(counts[id])
    if (!rvCounts) return null
    // Q = every list (api 678's "*", distinct patients) · T = the four extraction ages
    const lists = id === 'Q' ? ['*'] : id === 'T' ? EXTRACTION_LISTS : [id]
    return lists.reduce((sum, l) => sum + RV_KEYS.reduce((s2, k) => s2 + (Number(rvCounts[`${l}:${k}`]) || 0), 0), 0)
  }
  const today = bkkTodayISO()

  // ── the list picker model ──
  const EXT = ['G', 'C', 'D', 'F']
  const lastExtRef = useRef(EXT.includes(preset) ? preset : 'C')
  if (EXT.includes(preset)) lastExtRef.current = preset
  const tabKey = isView ? '' : (EXT.includes(preset) ? 'ext' : preset)
  const withCount = (text, n) => (
    <>{text}{n != null && Number.isFinite(n) ? <span className="tabular-nums text-slate-400 font-normal">{fmt(n)}</span> : null}</>
  )
  const extTotal = EXT.reduce((sum, k) => (countOf(k) == null ? sum : (sum ?? 0) + countOf(k)), null)
  const listTabs = [
    { key: 'W', label: withCount('Warm Case', countOf('W')), icon: Flame, title: presetById('W')?.hint },
    { key: 'A', label: withCount('มีนัดแล้ว', countOf('A')), title: presetById('A')?.hint },
    { key: 'ext', label: withCount('ถอนฟันแล้ว', extTotal), title: 'ถอนฟันที่คลินิก แยกตามระยะเวลาที่ถอน' },
    { key: 'E', label: withCount('ใส่ฟันปลอม', countOf('E')), title: presetById('E')?.hint },
    { key: 'B', label: withCount('ติดป้ายรากเทียม', countOf('B')), title: presetById('B')?.hint },
  ]
  const pickTab = (k) => {
    setView('')
    refilter(setPreset)(k === 'ext' ? lastExtRef.current : k)
  }
  const ageOptions = [
    { value: 'G', label: 'ภายใน 1 เดือน', count: countOf('G') ?? undefined, title: presetById('G')?.hint },
    { value: 'C', label: '2–6 เดือน', count: countOf('C') ?? undefined, title: presetById('C')?.hint },
    { value: 'D', label: '6–12 เดือน', count: countOf('D') ?? undefined, title: `${presetById('D')?.hint} · นอกไตรมาสนี้` },
    { value: 'F', label: '1–2 ปี', count: countOf('F') ?? undefined, title: `${presetById('F')?.hint} · นอกไตรมาสนี้` },
  ]
  // แท็บรอโทร's type filter — one kit Select (Ram, 2026-10-30: a dropdown, not a row of
  // buttons): everything first (the default), then one type; ถอนฟันแล้ว's ages are a
  // group at the end, its first choice every age (T).
  const cnt = (id) => (countOf(id) == null ? undefined : fmt(countOf(id)))
  const typeOptions = [
    { value: 'Q', label: 'ทั้งหมด', count: cnt('Q') },
    { value: 'W', label: 'Warm Case', count: cnt('W') },
    { value: 'L', label: 'ปรึกษาเกิน 6 เดือน', count: cnt('L') },
    { value: 'A', label: 'มีนัดแล้ว', count: cnt('A') },
    { value: 'E', label: 'ใส่ฟันปลอม', count: cnt('E') },
    { value: 'B', label: 'ติดป้ายรากเทียม', count: cnt('B') },
    { value: 'T', label: 'ทุกช่วง', selectedLabel: 'ถอนฟันแล้ว', count: cnt('T'), group: 'ถอนฟันแล้ว' },
    ...ageOptions.map((o) => ({ value: o.value, label: o.label, selectedLabel: `ถอนฟันแล้ว · ${o.label}`, count: cnt(o.value), group: 'ถอนฟันแล้ว' })),
  ]
  // R wears the snowflake of the พัก chips (ไปทำที่อื่นแล้ว · เคสไม่เหมาะ) that put people there
  const VIEW_ICON = { H: CalendarClock, P: PauseCircle, R: Snowflake }
  const viewTabs = VIEWS.map((v) => ({ key: v.id, label: withCount(v.id === 'R' ? 'รอรอบใหม่' : v.label, countOf(v.id)), icon: VIEW_ICON[v.id], title: v.hint }))
  // แท็บรีวิว's switch: รอรีวิว (the lists) or ไม่ต้องโทรตอนนี้ (the views), each with its
  // total. Switching back to the views reopens the one looked at last.
  const lastViewRef = useRef('H')
  if (viewById(view)) lastViewRef.current = view
  const queueTotal = rvCounts ? (Number(rvCounts['*:none']) || 0) + (Number(rvCounts['*:wait']) || 0) : null
  const viewTotal = VIEWS.reduce((sum, v) => (countOf(v.id) == null ? sum : (sum ?? 0) + countOf(v.id)), null)
  const sectionOptions = [
    { value: 'queue', label: 'รอรีวิว', count: queueTotal != null ? fmt(queueTotal) : undefined },
    { value: 'views', label: 'ไม่ต้องโทรตอนนี้', count: viewTotal != null ? fmt(viewTotal) : undefined },
  ]
  const pickSection = (sec) => refilter(setView)(sec === 'views' ? lastViewRef.current : '')
  // แท็บรีวิว's step picker, counted for this list (api 678 review_counts)
  const reviewOptions = REVIEW_STATUSES.filter((rs) => rs.id === 'none' || rs.id === 'wait').map((rs) => {
    const n = current?.review_counts ? Number(current.review_counts[rs.id]) : null
    return { value: rs.id, label: rs.label, title: rs.hint, count: n != null && Number.isFinite(n) ? fmt(n) : undefined }
  })
  // ล้างตัวกรอง — back to what a new browser starts with (the list and the step stay).
  const filtersAtDefault = !branchSel && (reviewMode || (preset === 'Q' && !reviewed))
  const clearFilters = () => {
    setBranchSel('')
    if (!reviewMode) { setPreset('Q'); setReviewed('') }
    setPage(1)
  }

  return (
    <>
      {/* WHICH LIST — kit SubTabs (level 2 under the page's Tabs), Ram 2026-10-29: "the
          filter is hectic". Five kinds of patient on the left; the four extraction-age
          lists are ONE tab with a Segmented for the age. The people not to call right
          now sit apart on the right. Counts are words in the label, not CountBadge,
          because the badge caps at 99+ and F holds two thousand. */}
      {/* แท็บรีวิว: a switch between the people to review and the people decided not to
          call right now (Ram, 2026-10-29), then that side's lists or views. แท็บรอโทร has
          no list tabs: every type in one list, the type is a filter below. */}
      {reviewMode && (
        <>
          <div className="mb-2.5">
            <Segmented options={sectionOptions} value={isView ? 'views' : 'queue'} onChange={pickSection} />
          </div>
          {isView
            ? <SubTabs items={viewTabs} value={view} onChange={refilter(setView)} />
            : <SubTabs items={listTabs} value={tabKey} onChange={pickTab} />}
          {tabKey === 'ext' && (
            <div className="mt-2.5">
              <Segmented size="sm" options={ageOptions} value={preset} onChange={(v) => { setView(''); refilter(setPreset)(v) }} />
            </div>
          )}
        </>
      )}
      <p className={`${reviewMode ? 'mt-2' : ''} text-[12.5px] text-slate-500`}>
        {meta?.hint}{meta?.sortNote ? <span className="text-slate-400"> · {meta.sortNote}</span> : null}
      </p>

      {/* THE FILTERS — one kit FilterCard. แท็บรีวิว picks the step; แท็บรอโทร has the
          type (ทั้งหมด by default; ถอนฟันแล้ว's ages inside the same dropdown) and
          "รีวิวล่าสุด". A view (nobody is called from it) has neither. */}
      <FilterCard compact className="mt-3" onClear={clearFilters} canClear={!filtersAtDefault}>
        {!reviewMode && (
          <Field label="ประเภท" className="min-w-[200px]">
            <Select value={preset} onChange={refilter(setPreset)} options={typeOptions} />
          </Field>
        )}
        <Field label="สาขา" className="min-w-[160px]">
          <BranchSelect value={branchSel} onChange={refilter(setBranchSel)} />
        </Field>
        {reviewMode && !isView && (
          <Field label="ขั้นรีวิว">
            <Segmented size="sm" options={reviewOptions} value={rvStatus} onChange={refilter(setRvStatus)} />
          </Field>
        )}
        {!reviewMode && (
          <Field label="รีวิวล่าสุด" className="min-w-[200px]">
            <Select value={reviewed} onChange={refilter(setReviewed)} placeholder="ไม่กรอง"
              options={REVIEWED_OPTS.map((o) => ({ value: o.v, label: o.label }))} />
          </Field>
        )}
      </FilterCard>

      {/* B is a preset here since 2026-09-17 (copied — /follow-ups/recall keeps its
          own filter). W is a list since 2026-10-29 (api 678): every consult that did
          not buy, so tab 2 tracks consults and this tab is where they get called. */}

      {st.err ? (
        <div className="mt-4"><Notice tone="error" text={`โหลดรายชื่อไม่สำเร็จ: ${st.err}`} /></div>
      ) : !current ? (
        <div className="mt-4">
          <Card icon={<Phone size={18} />} title="กำลังโหลด…">
            <div className="flex items-center justify-center gap-2 text-[13px] text-slate-400 h-[200px]">
              <Loader2 size={16} className="animate-spin" /> กำลังโหลดรายชื่อ…
            </div>
          </Card>
        </div>
      ) : (
        <div className="mt-4">
          <Card
            icon={<Phone size={18} />}
            title={isView ? `ไม่ต้องโทรตอนนี้ · ${meta?.label}`
              : `${meta?.label || `รายชื่อ ${preset}`} · ${reviewMeta(reviewSel).label}${reviewed ? ` · ${REVIEWED_OPTS.find((o) => o.v === reviewed)?.label}` : ''}`}
            badge={`${fmt(total)} ราย`}
            subtitle={`หน้า ${page} จาก ${pageTotal} · ครั้งละ ${PER_PAGE} ราย${st.loading ? ' · กำลังอัปเดต…' : ''}`}
          >
            {/* A silent suppression is how a hard rule quietly becomes a soft
                one. Say how many each rule removed from this list. */}
            {isView ? (
              <p className="mb-2 text-[12px] text-amber-700">
                {meta?.hint} · ไม่ต้องโทรเสนอรากเทียมตอนนี้ (recall ปกติยังดำเนินตามเดิม){view !== 'H' && ' — ถ้าต้องการโทรก่อนกำหนด เปิดคนไข้แล้วกด “ดึงกลับมาโทรตอนนี้” คนไข้จะย้ายไปแท็บรอโทร'}
              </p>
            ) : (excludedHidden > 0 || cancelledHidden > 0) && (
              <p className="mb-2 text-[12px] text-slate-500">
                ซ่อนจากลิสต์นี้{excludedHidden > 0 && ` ${fmt(excludedHidden)} รายที่พักไว้ รอรอบใหม่ หรือโทรแล้วรอ 3 วัน (${reviewMode ? 'ดูใน “ไม่ต้องโทรตอนนี้” ด้านบน' : 'ดูในแท็บรีวิว และแท็บผลการโทร'})`}
                {excludedHidden > 0 && cancelledHidden > 0 && ' ·'}
                {cancelledHidden > 0 && ` ${fmt(cancelledHidden)} รายที่สถานะยกเลิกเคส`}
              </p>
            )}

            {recheckHidden > 0 && (
              <p className="mb-2 text-[12px] text-slate-500">
                ซ่อน {fmt(recheckHidden)} รายในหน้านี้ที่เพิ่งมีการรักษาจัดฟันหรือรากเทียม — จะออกจากลิสต์หลังอัปเดตรอบถัดไป
              </p>
            )}
            {dataAsOf > 0 && (
              <p className={`mb-2 text-[11.5px] ${snapshotStale ? 'text-amber-700' : 'text-slate-400'}`}>
                {snapshotStale
                  ? `ประวัติการรักษาไม่ได้อัปเดตตั้งแต่ ${fmtDate(dataAsOf)} ${fmtTime(dataAsOf)} — แจ้งผู้ดูแลระบบ · นัด สถานะ และโน้ตยังเป็นข้อมูลล่าสุด`
                  : `ประวัติการรักษา ณ ${fmtDate(dataAsOf)} ${fmtTime(dataAsOf)} (อัปเดตทุกเช้า) · นัด สถานะ และโน้ตเป็นข้อมูลล่าสุด`}
              </p>
            )}

            {/* No CSV export on this page is still deliberate (plan.md §6.4 rule 1); the
                footer line saying so was removed on Ram's request, 2026-09-17. A remembered
                copy of another view is not clickable until the fresh answer lands. */}
            <DataTable
              columns={callColumns}
              fixed minWidth={reviewMode ? 880 : 1000}
              valign="top"
              rows={rows}
              rowKey={(r) => r.patient_id}
              onRowClick={fresh ? openRow : undefined}
              rowClassName={(r) => (worked.has(String(r.patient_id)) ? 'opacity-45' : '')}
              loading={st.loading}
              empty={{
                title: 'ไม่มีรายชื่อให้แสดง',
                hint:
                  total > 0
                      ? (recheckHidden > 0
                        ? `ทุกแถวในหน้านี้ถูกซ่อน เพราะเพิ่งมีการรักษาจัดฟันหรือรากเทียม — ${pageTotal > 1 ? 'ดูหน้าอื่น' : 'จะออกจากลิสต์หลังอัปเดตพรุ่งนี้เช้า'}`
                        : 'รายชื่อสั้นลง กำลังกลับไปหน้าสุดท้าย…')
                    // Blame "รีวิวล่าสุด" only if it removed someone: review_counts are
                    // counted before the review filters, so no พร้อมโทร at all means the
                    // list had nobody to call anyway.
                    : reviewFiltered && Number(current?.review_counts?.ready) > 0
                      ? 'ไม่พบรายชื่อที่ตรงกับตัวกรองรีวิวล่าสุด — เลือก “ไม่กรอง” เพื่อดูทั้งลิสต์'
                    : isView
                      ? `ยังไม่มีคนไข้ในมุมมอง “${meta?.label}”`
                    : reviewMode
                      ? `ไม่มีคน${rvStatus === 'wait' ? 'ที่รอตรวจซ้ำ' : 'ที่ยังไม่รีวิว'}ในลิสต์นี้`
                      : 'ยังไม่มีคนที่พร้อมโทรในลิสต์นี้ — คนไข้ต้องผ่านรีวิว 2 ขั้นในแท็บรีวิวก่อน · ถ้าเพิ่งจองนัดรากเทียมให้คนไข้ เขาจะย้ายไปอยู่ “รอวันนัด” โดยอัตโนมัติ',
              }}
              page={page} pageTotal={pageTotal} onPageChange={setPage} total={total}
            />
          </Card>
        </div>
      )}

      {/* ผลหลังรีวิวผ่าน moved to its own tab, ผลการโทร (2026-10-29). */}

      {selected && (
        <RecallPopup
          fullPageTab="treatments"
          key={selected.patient_id}
          patient={selected}
          me={me}
          onClose={closePopup}
          onLockAction={reviewMode ? undefined : popupLockAction}
          onChanged={reviewMode ? refetch : onNoteSaved}
          refreshKey={tick}
          notePrefix={RECALL_PREFIX(selected.list || preset)}
          quickCodes={reviewMode ? [] : chips}
          notesReadOnly={reviewMode}
          implantReview={view === 'H' ? '' : (selected.list || preset)}
          onReviewed={refetch}
        />
      )}
    </>
  )
}

/* What api 678 knows about this row's round (2026-10-29): the stage the appointment
 * book shows, why a parked patient is parked and until when, and for W the consult the
 * follow-up is about — who saw them, what was offered, and where the 21/45/90-day rhythm
 * stands. Empty for an ordinary row. */
function RowDetail({ r, today }) {
  const stage = r.stage && STAGES[r.stage]
  const parts = []
  if (stage) {
    let extra = ''
    if (r.stage === 'waiting') extra = `${r.stage_date ? ` ${fmtDay(r.stage_date)}` : ''}${HOLD_TYPE_LABEL[r.stage_type] ? ` · ${HOLD_TYPE_LABEL[r.stage_type]}` : ''}`
    else if (r.stage === 'back_from_park' || r.stage === 'retry') extra = ` · ${parkReasonLabel(r.last_chip, OUTCOME_BY_CODE)}${r.last_chip_at ? ` (${fmtDate(r.last_chip_at)})` : ''}`
    else if (r.stage_date) extra = ` ${fmtDay(r.stage_date)}`
    parts.push(<Badge key="st" tone={stage.tone} className="mr-1.5">{stage.label}{extra}</Badge>)
  }
  if (r.park_kind === 'P') {
    const left = r.park_until ? daysSince(today, r.park_until) : null
    parts.push(<span key="pk" className="mr-1.5 text-[11.5px] text-amber-800">พักไว้ · {parkReasonLabel(r.park_reason, OUTCOME_BY_CODE)}{r.park_until ? ` · กลับ ${fmtDay(r.park_until)}${left != null ? ` (อีก ${left} วัน)` : ''}` : ''}</span>)
  } else if (r.park_kind === 'R' && !r.excluded_code) {
    parts.push(<span key="pk" className="mr-1.5 text-[11.5px] text-sky-700">{parkReasonLabel(r.park_reason, OUTCOME_BY_CODE)} · กลับมาเมื่อถอนฟันซี่ใหม่หรือมาปรึกษาใหม่</span>)
  }
  // W and its cold step L (ปรึกษาเกิน 6 เดือน) carry the consult; the 21 · 45 · 90 cadence
  // is W's alone
  if (r.list === 'W' || r.list === 'L') {
    const d = daysSince(r.consult_date, today)
    const c = r.list === 'W' ? cadenceHint(d) : null
    parts.push(
      <span key="w" className="block text-[11.5px] text-slate-600">
        {r.consult_doctor ? `หมอ${r.consult_doctor}` : 'ปรึกษา'}{d != null ? ` · ${d} วันก่อน` : ''}
        {c && <span className="text-amber-700">{c.over ? ' · เกิน 90 วัน' : ` · ครบ ${c.point} วันในอีก ${c.inDays} วัน`}</span>}
        {r.consult_offer && <span className="text-slate-500"> · {r.consult_offer}</span>}
      </span>,
    )
    if (r.consult_note) parts.push(<span key="wn" className="block truncate text-[11px] text-slate-400" title={r.consult_note}>{r.consult_note}</span>)
  }
  return parts.length ? <>{parts}</> : null
}

/* The รีวิว cell: status pill, and for พร้อมโทร what happened after approval (api 650
 * call_outcome — this page only). Who/when for each step is in the tooltip.
 * `hideStatus` is แท็บรอโทร, where every row is พร้อมโทร and waiting for the call: it
 * drops the status pill and ยังไม่โทร (Ram, 2026-10-30 — both say nothing there), keeps a
 * call that happened; nothing at all when there is nothing else to say. */
export function ReviewCell({ r, hideStatus = false }) {
  const status = r.review_status || 'none'
  const o = status === 'ready' && !(hideStatus && r.call_outcome === 'not_called') ? OUTCOME_BY_ID[r.call_outcome] : null
  if (hideStatus && !o) return null
  if (status === 'none') return <span className="text-[11px] text-slate-300 whitespace-nowrap">ยังไม่รีวิว</span>
  const m = reviewMeta(status)
  const line = (step, res, by, at) => (res ? `${STEP_LABEL[step]} ${RESULT_LABEL[res] || res}${by ? ` · ${by}` : ''} · ${fmtDate(at)} ${fmtTime(at)}` : '')
  const title = [line(1, r.s1_result, r.s1_by_code, r.s1_at), line(2, r.s2_result, r.s2_by_code, r.s2_at)].filter(Boolean).join('\n')
  return (
    <span title={title} className="inline-flex flex-col items-start gap-0.5">
      {!hideStatus && (
        <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-md px-1.5 py-0.5 text-[10.5px] ring-1 ${m.pill}`}>
          <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${m.dot}`} />{m.label}
        </span>
      )}
      {o && (
        <span className={`whitespace-nowrap rounded px-1 py-px text-[10px] ring-1 ${o.pill}`}>
          {o.label}{r.success_consult ? ' · ปรึกษา' : ''}{r.paid ? ' · จ่ายแล้ว' : ''}
        </span>
      )}
      {o && r.bk_no_booking && <span className="whitespace-nowrap text-[10px] text-amber-700">⚠ จองแล้ว · ไม่พบนัด</span>}
    </span>
  )
}
